import { formatDateTime, useApiErrorMessage, useTranslation } from "@roastery/i18n";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  PageHeader,
  toast,
  useConfirm,
} from "@roastery/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ShoppingCartIcon, Trash2Icon } from "lucide-react";
import { useDeletePortalTemplate, usePortalTemplates, type OrderTemplate } from "../lib/portal-api";
import { useReorder } from "../lib/use-reorder";
import { useShelfLines } from "../lib/use-shelf-lines";
import { ShelfLineLabel } from "../components/shelf-line-label";
import { usePortalFormatSettings } from "../lib/use-portal-format";

export const Route = createFileRoute("/_authed/templates")({
  component: TemplatesPage,
});

/**
 * The customer's saved shopping lists (US-10.4).
 *
 * <p>A list is not a cart and not an order: it says <em>what</em>, never <em>how much it costs</em>.
 * Prices are joined here from today's shelf, so a list saved in March shows September's price — and
 * a coffee that has since left the range is marked rather than quietly missing, because a buyer who
 * drops the Tuesday list into the cart deserves to be told which line stopped coming.
 */
function TemplatesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const settings = usePortalFormatSettings();
  const templates = usePortalTemplates();
  const lookUp = useShelfLines();
  const reorder = useReorder();
  const remove = useDeletePortalTemplate();
  const errorMessage = useApiErrorMessage();
  const confirm = useConfirm();

  const onDelete = async (template: OrderTemplate) => {
    const confirmed = await confirm({
      title: t("portal.templates.confirmDelete"),
      description: t("portal.templates.confirmDeleteBody", { name: template.name }),
      confirmLabel: t("common.delete"),
      cancelLabel: t("common.cancel"),
      destructive: true,
    });
    if (!confirmed) {
      return;
    }
    remove.mutate(
      { params: { path: { templateId: template.id ?? "" } } },
      {
        onSuccess: () => toast.success(t("portal.templates.deleted", { name: template.name })),
        onError: (error) => toast.error(errorMessage(error)),
      },
    );
  };

  if (templates.isLoading) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="portal-templates-loading">
        {t("app.loading")}
      </p>
    );
  }

  const rows = templates.data ?? [];

  return (
    <div className="flex flex-col gap-4" data-testid="portal-templates">
      <div>
        <PageHeader title={t("portal.templates.title")} />
        <p className="text-sm text-muted-foreground">{t("portal.templates.description")}</p>
      </div>

      {rows.length === 0 ? (
        <Card data-testid="portal-templates-empty">
          <CardHeader>
            <CardDescription>{t("portal.templates.empty")}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button data-testid="portal-back-to-catalog" onClick={() => void navigate({ to: "/" })}>
              {t("portal.cart.placed.backToCatalog")}
            </Button>
          </CardFooter>
        </Card>
      ) : (
        rows.map((template) => {
          const lines = template.lines ?? [];
          const resolved = lookUp(lines);
          const onShelf = new Map(resolved.lines.map((line) => [line.variantId, line]));
          return (
            <Card key={template.id} data-testid={`portal-template-${template.id}`}>
              <CardHeader>
                <CardTitle className="flex flex-wrap items-center gap-2">
                  {template.name}
                  <Badge variant="muted">{t("portal.templates.lines", { count: lines.length })}</Badge>
                </CardTitle>
                <CardDescription>
                  {template.authorName ? t("portal.templates.author", { name: template.authorName }) : null}
                  {template.authorName && template.updatedAt ? " · " : null}
                  {template.updatedAt
                    ? t("portal.templates.updatedAt", { date: formatDateTime(template.updatedAt, settings) })
                    : null}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="flex flex-col gap-1 text-sm">
                  {lines.map((line) => (
                    <li
                      key={line.variantId}
                      className="flex items-center justify-between gap-2"
                      data-testid={`portal-template-line-${line.sku}`}
                    >
                      <ShelfLineLabel line={line} onShelf={line.variantId ? onShelf.get(line.variantId) : undefined} />
                      {line.variantId && onShelf.has(line.variantId) ? null : (
                        <Badge variant="warning" data-testid={`portal-template-withdrawn-${line.sku}`}>
                          {t("portal.templates.withdrawn")}
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
                {resolved.missing.length > 0 ? (
                  <p
                    className="mt-3 text-xs text-muted-foreground"
                    data-testid={`portal-template-note-${template.id}`}
                  >
                    {t("portal.templates.withdrawnNote")}
                  </p>
                ) : null}
              </CardContent>
              <CardFooter className="gap-2">
                <Button
                  data-testid={`portal-template-add-${template.id}`}
                  disabled={!resolved.ready}
                  onClick={() => reorder(lines)}
                >
                  <ShoppingCartIcon aria-hidden />
                  {t("portal.templates.addToCart")}
                </Button>
                <Button
                  variant="ghost"
                  data-testid={`portal-template-delete-${template.id}`}
                  onClick={() => void onDelete(template)}
                >
                  <Trash2Icon aria-hidden />
                  {t("portal.templates.delete")}
                </Button>
              </CardFooter>
            </Card>
          );
        })
      )}
    </div>
  );
}
